// Hide an image that fails to load, without an inline event handler.
//
// Two pages carried `onerror="this.style.display='none'"` on a decorative
// image — the FUBAR header icon and the status page's Traffic Scotland logo.
// An inline event handler is inline script, and the production policy is
// `script-src 'self'` with no `'unsafe-inline'`, so the browser refused to run
// either of them on scot.live and nowhere else. Both were therefore doing
// nothing in production for their whole lives, in the quietest possible way:
// the handler only runs once the image is already broken, so the failure it
// was written for is the only moment anybody would have seen it not work, and
// what they would have seen is the broken-image marker it was there to hide —
// on the status page, in the middle of a paragraph about who publishes the
// data, on a white plate that shows it clearly.
//
// Found on the first run of the preview CSP (#65), by a browser, in about four
// seconds, which is the second time that has been the sentence: the Cesium
// directives were the first.
//
// The attribute is not replaced with something similar; it is replaced with a
// listener, because that is what the policy permits and what a module can be
// tested against.
//
// It happened a third time (#74): the Roads box credit, the FUBAR popup icon
// and the control-room footer logo all carried the same dead attribute in
// template strings, two of them injected long after page load where
// hideBrokenImages() below has already run. watchBrokenImages() is the answer
// for that shape — one capture-phase listener on the document, covering every
// tagged image that exists now or arrives later — and test-csp.mjs now
// refuses the attribute anywhere in the source, so the fourth recurrence
// fails a test instead of shipping.

/** The images this handles: `<img data-hide-on-error>`. */
export const HIDE_ON_ERROR = 'img[data-hide-on-error]';

/**
 * Hides any decorative image that has failed, and any that fails later.
 *
 * Both halves are needed, and the first is the one that is easy to leave out.
 * Page modules are `type="module"` and so deferred, while the images start
 * loading as the parser meets them — so by the time this runs, a broken image
 * has usually already fired its `error` event at nobody. `complete` with a
 * `naturalWidth` of zero is that state after the fact, and without checking it
 * this would work perfectly against a slow network and not at all against a
 * fast one.
 */
export function hideBrokenImages(root: ParentNode = document): void {
  for (const img of root.querySelectorAll<HTMLImageElement>(HIDE_ON_ERROR)) {
    if (img.complete && img.naturalWidth === 0) img.style.display = 'none';
    img.addEventListener('error', () => { img.style.display = 'none'; });
  }
}

/**
 * Hides every tagged image that ever fails on this page, including ones that
 * do not exist yet.
 *
 * The map page needs this where the static pages do not: popups and the
 * control-room overlay build their `<img>` tags from template strings minutes
 * after load, so a per-element pass at boot has nothing to attach to. `error`
 * does not bubble, but it is observable in the capture phase on the document,
 * which sees the event on the way down to any descendant — one listener,
 * every current and future image, no per-injection bookkeeping.
 */
export function watchBrokenImages(doc: Document = document): void {
  doc.addEventListener('error', (e) => {
    const t = e.target;
    if (t instanceof HTMLImageElement && t.matches(HIDE_ON_ERROR)) {
      t.style.display = 'none';
    }
  }, true);
}
